import { Viewer } from './viewer';

import * as THREE from 'three';

export type Layer = {
  identifier: string;
  url: string;
  layerName: string;
  type: 'WMS' | 'WMTS';
  username?: string | null;
  password?: string | null;
};

export class MapOverlay {
  isMounted = false;
  private mapVisible = false;
  private olMap: ol.Map | null = null;
  private layers: Layer[] = [];
  private olLayersByIdentifier: { [key: string]: ol.layer.Tile } = {};
  private viewer: Viewer;

  constructor({ viewer }: { viewer: Viewer }) {
    this.viewer = viewer;
    this.toggleMount = this.toggleMount.bind(this);
    this.render = this.render.bind(this);
    this.addWmsLayer = this.addWmsLayer.bind(this);
    this.mountMapOverlay = this.mountMapOverlay.bind(this);
    this.removeLayerFromOlMap = this.removeLayerFromOlMap.bind(this);
    this.showMap = this.showMap.bind(this);
    this.hideMap = this.hideMap.bind(this);
    this.unmountMapOverlay = this.unmountMapOverlay.bind(this);
    this.removeWmsLayer = this.removeWmsLayer.bind(this);
    this.renderWmsLayer = this.renderWmsLayer.bind(this);
  }

  showMap() {
    if (this.mapVisible) return;
    this.mapVisible = true;
    if (!this.isMounted) this.mountMapOverlay();
    this.olMap?.addLayer(new ol.layer.Tile({ source: new ol.source.OSM(), name: 'map_id' }));
    // This is to fix the order of layers when toggling the map visibility
    const currentLayers = [...this.layers];
    currentLayers.forEach((layer) => {
      this.removeWmsLayer({ identifier: layer.identifier });
      this.addWmsLayer(layer);
    });
  }

  hideMap() {
    if (!this.mapVisible) return;
    this.mapVisible = false;
    this.removeLayerFromOlMap({ identifier: 'map_id' });
    if (this.isMounted && this.layers.length <= 0) this.unmountMapOverlay();
  }

  addWmsLayer(layer: Layer) {
    if (this.layers.some((l) => l.identifier === layer.identifier)) return;
    this.layers.push(layer);
    if (!this.isMounted) this.mountMapOverlay();
    this.renderWmsLayer(layer);
  }

  private removeLayerFromOlMap({ identifier }: { identifier: string }) {
    this.olMap
      ?.getLayers()
      .getArray()
      .slice()
      .forEach((layer: ol.OlLayer) => {
        if (layer.get('name') === identifier) {
          // For some reason, it does not work to remove the layer directly from the map using the reference in this.olLayersByIdentifier
          // (test scenario: Have 2 layers not active, toggle layer A, toggle layer B, toggle layer A again, toggle layer B again)
          this.olMap?.removeLayer(layer);
        }
      });
  }

  removeWmsLayer({ identifier }: { identifier: string }) {
    this.layers = this.layers.filter((layer) => layer.identifier !== identifier);
    this.removeLayerFromOlMap({ identifier });
    delete this.olLayersByIdentifier[identifier];
    if (this.layers.length <= 0 && !this.mapVisible) this.unmountMapOverlay();
  }

  async toggleMount() {
    const viewer = this.viewer;
    if (!this.isMounted) {
      this.mountMapOverlay();
    } else {
      this.unmountMapOverlay();
    }
    viewer.dispatchEvent({
      type: 'map_overlay_changed',
    });
  }

  async render() {
    const viewer = this.viewer;

    const coordinatesToOL = (position: { x: number; y: number }, pointcloudProjection: string): [number, number] => {
      const xy = [isNaN(position.x) ? 0 : position.x, isNaN(position.y) ? 0 : position.y];
      const transformedCoords = proj4(pointcloudProjection, 'EPSG:3857', xy);

      return transformedCoords;
    };

    const updateMapView = () => {
      if (!this.olMap) return;
      if (!this.mapVisible && !this.layers.length) return;
      const pointcloudProjection = viewer.scene.pointclouds[0]?.projection;
      if (!pointcloudProjection) return;
      const activeCamera = viewer.scene.getActiveCamera();
      if (!(activeCamera instanceof THREE.OrthographicCamera)) return;
      const camera = activeCamera;
      const gExtent = new ol.geom.LineString([
        [0, 0],
        [0, 0],
      ]);
      const view = this.olMap.getView();

      const yaw = camera.rotation.z;
      const center = camera.position;

      const mapExtent = {
        bottomLeft: coordinatesToOL(new THREE.Vector2(center.x + camera.left, center.y + camera.bottom).rotateAround(center, yaw), pointcloudProjection),
        bottomRight: coordinatesToOL(new THREE.Vector2(center.x + camera.right, center.y + camera.bottom).rotateAround(center, yaw), pointcloudProjection),
        topRight: coordinatesToOL(new THREE.Vector2(center.x + camera.right, center.y + camera.top).rotateAround(center, yaw), pointcloudProjection),
        topLeft: coordinatesToOL(new THREE.Vector2(center.x + camera.left, center.y + camera.top).rotateAround(center, yaw), pointcloudProjection),
      };

      gExtent.setCoordinates([mapExtent.bottomLeft, mapExtent.bottomRight, mapExtent.topRight, mapExtent.topLeft]);
      // For some reason setRotation does not work for small rotations, hence adding of 2*PI
      view.setRotation(Math.abs(yaw) > 0.1 ? yaw : yaw + 2 * Math.PI);
      view.fit(gExtent, { size: this.olMap.getSize() });
    };

    const loop = () => {
      requestAnimationFrame(loop);
      updateMapView();
    };
    loop();
  }

  async renderWmsLayer(layer: Layer) {
    const { url, type, layerName, username, password } = layer;
    if (!this.olMap) return;
    let authorization = '';
    if (username || password) {
      authorization = `Basic ${window.btoa(`${username}:${password}`)}`;
    }
    const fetchOptions = authorization ? { headers: { Authorization: authorization } } : undefined;
    const capabilitiesRaw = await new Promise<string>((resolve) => fetch(url, fetchOptions).then((response) => resolve(response.text())));
    if (type === 'WMTS') {
      const capabilities = new ol.format.WMTSCapabilities().read(capabilitiesRaw);
      const options = ol.source.WMTS.optionsFromCapabilities(capabilities, { layer: layerName });
      const olLayer = new ol.layer.Tile({
        name: layer.identifier,
        opacity: 1,
        source: new ol.source.WMTS(options),
      });

      this.olLayersByIdentifier[layer.identifier] = olLayer;
      this.olMap?.addLayer(olLayer);
    } else if (type === 'WMS') {
      const capabilities = new ol.format.WMSCapabilities().read(capabilitiesRaw);
      const url = capabilities.Capability.Request.GetMap.DCPType[0].HTTP.Get.OnlineResource;
      const options = {
        url,
        params: { layers: layerName },
        tileLoadFunction:
          username &&
          ((tile: { getImage(): HTMLImageElement }, src: string) => {
            const client = new XMLHttpRequest();
            client.responseType = 'arraybuffer';
            client.open('GET', src);
            client.setRequestHeader('Authorization', authorization);
            client.onload = function () {
              const arrayBufferView = new Uint8Array(this.response);
              const blob = new Blob([arrayBufferView], { type: 'image/png' });
              const urlCreator = window.URL || (window as unknown as { webkitURL: typeof URL }).webkitURL;
              const imageUrl = urlCreator.createObjectURL(blob);
              tile.getImage().src = imageUrl;
            };
            client.send();
          }),
      };
      const olLayer = new ol.layer.Tile({
        name: layer.identifier,
        opacity: 1,
        source: new ol.source.TileWMS(options),
      });
      this.olLayersByIdentifier[layer.identifier] = olLayer;
      this.olMap?.addLayer(olLayer);
    } else {
      console.error('Unknown layer type', layerName, type);
    }
  }

  private mountMapOverlay() {
    this.olMap = new ol.Map({
      target: 'olContainer',
      layers: [],
      view: new ol.View({}),
    });
    this.olMap.getControls().forEach((control: ol.OlControl) => {
      this.olMap?.removeControl(control);
    });
    this.olMap.render();
    this.render();
    this.isMounted = true;
  }

  private unmountMapOverlay() {
    if (!this.olMap) return;
    this.olMap.setTarget('');
    this.olMap = null;
    this.isMounted = false;
  }
}
